import {
    type BootstrappedFlags,
    ReflagBootstrappedProvider,
    type ReflagProps,
    ReflagProvider,
    useClient,
} from '@reflag/react-sdk';
import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import clsx from 'clsx';
import { Provider } from 'jotai';
import * as React from 'react';

import { withReflag } from '@gitbook/adaptive';
import type { User } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { isGitBookStaff } from '@gitbook/app-database';
import type { EnabledFeatureFlagsMap } from '@gitbook/app-featureflags';
import type { NavigationScreenOrgSettings, NavigationScreenState } from '@gitbook/app-navigation';
import { checkIsEnterpriseProductKey } from '@gitbook/app-pricing';
import { env, webConfig } from '@gitbook/env';
import { IconsProvider } from '@gitbook/icons';
import { UIKitProvider } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ConfirmProvider,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    EmptyStateNotFoundHeading,
    GitBookLoader,
    ImageProvider,
    RouterProvider,
} from '@gitbook/spine-react';
import { SentryTags } from '@gitbook/util-debug';

import { AppLayout, AppToaster, OfflineOverlay } from './AppLayout';
import { AgentDock, SiteQualityCustomerIOActions } from './Components';
import { AgentProvider } from './Components/Agent';
import { APIErrorBoundary } from './Components/APIErrorBoundary';
import { AppSearch } from './Components/AppSearch';
import { DndProvider } from './Components/DragAndDrop';
import {
    ErrorScreen,
    enableSentryReplay,
    setSentryContext,
    useSetSentryTag,
} from './Components/Errors';
import { HiveAppProvider } from './Components/Hive';
import { IconSourcesProvider, useInlineIconSources } from './Components/Icons';
import { ImportRunToasts } from './Components/Imports';
import { MediaGallery } from './Components/MediaGallery';
import { NotificationsProvider } from './Components/Notifications';
import {
    OrganizationAccessFallback,
    OrganizationAccessPlaceholder,
    OrganizationNotFoundEmptyState,
} from './Components/Organization';
import { useHideSplashscreen } from './Components/Splashscreen';
import { UpgradeDialogProvider } from './Components/UpgradeDialog';
import {
    HistoryProvider,
    type NavigateOptions,
    NavigationProvider,
    NavigationRedirect,
    NavigationScreenRenderer,
    type NavigationTarget,
    useNavigationFunctions,
    useNavigationState,
    useNeighborPreload,
} from './navigation';
import {
    AgentsScreen,
    ChangeRequestsScreen,
    CollectionScreen,
    ContentGapsScreen,
    EnforceSSOWrapper,
    getScreenNeighbors,
    HomeScreen,
    InvitesOrganizationScreen,
    JoinOrganizationScreen,
    IntegrationsLegacyRedirect,
    LinkAuditScreen,
    LoginScreenWrapper,
    OAuthAuthorizeScreen,
    OnboardingFlow,
    OrganizationContentScreen,
    RedirectOrgWithBasename,
    RedirectWithSitePreview,
    RootRedirect,
    SettingsScreen,
    SiteInviteScreen,
    SiteScreen,
    SitesListScreen,
    SpaceScreen,
    StyleguideNewScreen,
    StyleguidesScreen,
    TrashScreen,
    TriageScreen,
} from './Screens';
import { SitePlanDialogs } from './Screens/Site/components/Plan/SitePlanDialogs';
import { TriageProcessingToasts } from './Screens/Triage/TriageContext';
import { ContentSidebar, SettingsSidebar } from './Sidebars';
import {
    apiClient,
    apiQuery,
    apiQueryClient,
    apiQueryPersistOptions,
    autoStartLogSession,
    CurrentOrganizationProvider,
    CurrentUserProvider,
    FirebaseAuthProvider,
    PersistedStateMigrationProvider,
    UserDataProvider,
    useCurrentOrganizationId,
    useCustomerIoInAppMessages,
    useLoggedInUser,
    usePersistOnboardingParamsFromURL,
    useRealtimeChannelsReliability,
    useUpdateOrgMemberLastSeenAt,
} from './state';
import { getFeatureFlagTestOverrides } from './testing/featureflags';
import { getImageResizedURL, useDevelopmentLiveReload } from './utils';

autoStartLogSession();

function UIKitProviderWithPaletteMenuFeature(props: { children: React.ReactNode }) {
    return <UIKitProvider>{props.children}</UIKitProvider>;
}

/**
 * Entry point component.
 */
export function Application() {
    // Capture and persist Onboarding params from the URL, if present
    usePersistOnboardingParamsFromURL();

    // Worst case scenario, we always hide the splashscreen after 3 seconds
    // to ensure it doesn't get stuck on an unhandled route
    useHideSplashscreen(4000, true);
    useDevelopmentLiveReload();
    const iconSourceAssets = React.useMemo(
        () => ({
            assetsURL: 'https://ka-p.fontawesome.com/releases/v7.2.0/',
            assetsURLToken: webConfig.icons.token,
            assetsByStyles: {
                'custom-icons': {
                    assetsURL: '/public/icons/',
                },
            },
        }),
        []
    );
    const { iconSources, loadIconSources } = useInlineIconSources(iconSourceAssets);

    return (
        <React.Suspense fallback={<FullScreenLoadingFallback />}>
            <ImageProvider getResizedURL={getImageResizedURL}>
                <PersistQueryClientProvider
                    client={apiQueryClient}
                    persistOptions={apiQueryPersistOptions}
                >
                    <Provider>
                        <ErrorScreen>
                            <React.Suspense fallback={<FullScreenLoadingFallback />}>
                                <PersistedStateMigrationProvider>
                                    <IconsProvider
                                        assetsURL={iconSourceAssets.assetsURL}
                                        assetsURLToken={iconSourceAssets.assetsURLToken}
                                        assetsByStyles={iconSourceAssets.assetsByStyles}
                                        iconSources={iconSources}
                                    >
                                        <IconSourcesProvider load={loadIconSources}>
                                            <ConfirmProvider>
                                                <UIKitProviderWithPaletteMenuFeature>
                                                    <DndProvider>
                                                        <HistoryProvider>
                                                            <NavigationProvider persist>
                                                                <ScreenNavigationRouterProvider>
                                                                    <FirebaseAuthProvider>
                                                                        <CurrentUserProvider>
                                                                            <HiveAppProvider>
                                                                                <LoginScreenWrapper>
                                                                                    <UserDataProvider>
                                                                                        <ApplicationWithOrganization>
                                                                                            <AgentProvider>
                                                                                                <MainApplication />
                                                                                            </AgentProvider>
                                                                                        </ApplicationWithOrganization>
                                                                                    </UserDataProvider>
                                                                                </LoginScreenWrapper>
                                                                                <AppToaster />
                                                                            </HiveAppProvider>
                                                                        </CurrentUserProvider>
                                                                    </FirebaseAuthProvider>
                                                                </ScreenNavigationRouterProvider>
                                                            </NavigationProvider>
                                                        </HistoryProvider>
                                                        <OfflineOverlay />
                                                    </DndProvider>
                                                </UIKitProviderWithPaletteMenuFeature>
                                            </ConfirmProvider>
                                        </IconSourcesProvider>
                                    </IconsProvider>
                                </PersistedStateMigrationProvider>
                            </React.Suspense>
                        </ErrorScreen>
                    </Provider>
                </PersistQueryClientProvider>
            </ImageProvider>
        </React.Suspense>
    );
}

export type Href = NavigationTarget | string;

declare module '@gitbook/spine-react' {
    interface RouterConfig {
        href: Href;
        routerOptions: NavigateOptions;
    }
}

/**
 * Provider to navigate to a screen when a link is clicked in Spine components.
 */
function ScreenNavigationRouterProvider(props: { children: React.ReactNode }) {
    const { navigate, getNavigationURL } = useNavigationFunctions();
    return (
        <RouterProvider
            navigate={(href, options) => {
                if (typeof href === 'string') {
                    window.location.href = href;
                } else {
                    navigate(href, options);
                }
            }}
            useHref={(href: Href) => {
                if (typeof href === 'string') {
                    return href;
                }
                return getNavigationURL(href);
            }}
        >
            {props.children}
        </RouterProvider>
    );
}

/**
 * Check if the focus is in an editable element.
 */
function isFocusInEditable(): boolean {
    const { activeElement } = document;

    if (!activeElement || !(activeElement instanceof HTMLElement)) {
        return false;
    }

    if (activeElement.isContentEditable) {
        return true;
    }

    if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
        return !activeElement.readOnly && !activeElement.disabled;
    }

    if (activeElement instanceof HTMLSelectElement) {
        return !activeElement.disabled;
    }

    return false;
}

const autoFeedbackHandler: NonNullable<ReflagProps['feedback']>['autoFeedbackHandler'] = (
    message,
    handlers
) => {
    if (!isFocusInEditable()) {
        handlers.openFeedbackForm({
            title: message.question,
        });
    }
};

/**
 * Application component being rendered when an user is logged in.
 */
const MainApplication = React.memo(function MainApplication() {
    useCustomerIoInAppMessages();
    useRealtimeChannelsReliability();

    return (
        <FeatureFlagsProvider>
            <TrackFeatureFlags />
            {/* Temp: added for the mkt quality campaign - will be cleaned once it ends - RND-13295 */}
            <SiteQualityCustomerIOActions />
            <NotificationsProvider>
                <div className="flex min-h-0 flex-1 flex-col bg-side">
                    <NavigationScreenRenderer
                        Screen={ScreenRouter}
                        fallback={<FullScreenLoadingFallback />}
                    />
                    {/* Below the screen rather than inside it, so a conversation survives
                        navigation and is never reloaded, and the app reserves room for the dock
                        instead of the dock covering the screen. Under the router it would also be
                        rendered twice while an overlay screen is open. */}
                    <AgentDock />
                    <AppSearch />
                </div>
                <MediaGallery />
                <SitePlanDialogs />
                <ImportRunToasts />
                <TriageProcessingToasts />
            </NotificationsProvider>
        </FeatureFlagsProvider>
    );
});

/**
 * Provides Reflag feature flags to the logged-in application.
 */
function FeatureFlagsProvider(props: { children: React.ReactNode }) {
    const currentUser = useLoggedInUser();
    const { screen } = useNavigationState();

    const testFeatureFlagOverrides = React.useMemo(
        () => (env === 'test' ? getFeatureFlagTestOverrides() : []),
        []
    );

    // Match the UTC signup date stored through the server-side analytics identify flow.
    const userSignUpDate = React.useMemo(() => {
        const creationTime = currentUser.metadata.creationTime;
        if (!creationTime) {
            return undefined;
        }

        const createdAt = new Date(creationTime);
        if (Number.isNaN(createdAt.getTime())) {
            return undefined;
        }

        return createdAt.toISOString().slice(0, 10);
    }, [currentUser.metadata.creationTime]);

    const reflagContext = React.useMemo(
        () => ({
            user: {
                id: currentUser.uid,
                email: currentUser.email ?? undefined,
                name: currentUser.displayName ?? '',
                userSignUpDate,
            },
            company:
                'organization' in screen && screen.organization
                    ? {
                          id: screen.organization,
                      }
                    : undefined,
        }),
        [currentUser.displayName, currentUser.email, currentUser.uid, screen, userSignUpDate]
    );

    const toolbar =
        (isGitBookStaff(currentUser) || env === 'development') &&
        !process.env.GITBOOK_PREDICTABLE_UI;

    if (env === 'test') {
        return (
            <ReflagBootstrappedProvider
                publishableKey={webConfig.bucketCo.publishableKey}
                flags={getTestFeatureFlags(testFeatureFlagOverrides, reflagContext)}
                offline
                toolbar={toolbar}
                feedback={{ autoFeedbackHandler }}
            >
                {props.children}
            </ReflagBootstrappedProvider>
        );
    }

    return (
        <ReflagProvider
            publishableKey={webConfig.bucketCo.publishableKey}
            context={reflagContext}
            toolbar={toolbar}
            feedback={{ autoFeedbackHandler }}
        >
            {props.children}
        </ReflagProvider>
    );
}

/**
 * Builds bootstrapped Reflag flags for Playwright feature flag overrides.
 */
function getTestFeatureFlags(
    flags: string[],
    context: BootstrappedFlags['context']
): BootstrappedFlags {
    return {
        context,
        flags: Object.fromEntries(
            flags.map((flag) => [
                flag,
                {
                    key: flag,
                    isEnabled: true,
                },
            ])
        ) as BootstrappedFlags['flags'],
    };
}

function ApplicationWithOrganization({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const { screen } = useNavigationState();

    const currentOrgId =
        screen.type !== 'org_join' && 'organization' in screen
            ? (screen.organization ?? null)
            : null;

    // `getOrganizationById` returns 404 when the org doesn't exist or the user has no permission
    // to see it. The boundary surfaces the [[OrganizationNotFoundEmptyState]] in both cases. We
    // key the boundary by `currentOrgId` so navigating to a different org resets it.
    return (
        <APIErrorBoundary
            fallback={{ 404: <OrganizationNotFoundEmptyState /> }}
            resetKeys={[currentOrgId]}
        >
            <ApplicationWithOrganizationInner currentOrgId={currentOrgId}>
                {children}
            </ApplicationWithOrganizationInner>
        </APIErrorBoundary>
    );
}

function ApplicationWithOrganizationInner({
    children,
    currentOrgId,
}: {
    children: React.ReactNode;
    currentOrgId: string | null;
}): React.ReactElement {
    const user = useLoggedInUser();
    const [{ data: organization }, { data: organizationMember }, { data: authenticatedUser }] =
        useSuspenseQueries({
            queries: [
                apiQuery(
                    'getOrganizationById',
                    currentOrgId ? { organizationId: currentOrgId } : skipToken
                ),
                // Organization lists are paginated, so access must use the exact membership.
                apiQuery(
                    'getMemberInOrganizationById',
                    currentOrgId ? { organizationId: currentOrgId, userId: user.uid } : skipToken,
                    { nullOnError: true }
                ),
                // We don't care about the data, but the query will ensure the DB profile
                // for the authenticated user and its permissions are created after a signup
                apiQuery('getAuthenticatedUser', {}),
            ],
        });

    const userCannotAccessCurrentOrg = Boolean(currentOrgId && !organizationMember);

    // Track the organization context in Sentry
    React.useEffect(
        () =>
            setSentryContext({
                organization:
                    organization && !userCannotAccessCurrentOrg
                        ? {
                              id: organization.id,
                              title: organization.title,
                              type: organization.type,
                              plan: organization.plan,
                          }
                        : null,
            }),
        [organization, userCannotAccessCurrentOrg]
    );

    useSetSentryTag(
        SentryTags.ORGANIZATION_PLAN,
        userCannotAccessCurrentOrg || !organization ? null : organization.plan
    );

    // Record session in Sentry replay for enterprise customers
    const organizationPlan = organization?.plan;
    React.useEffect(() => {
        if (
            organizationPlan &&
            checkIsEnterpriseProductKey(organizationPlan) &&
            !userCannotAccessCurrentOrg
        ) {
            enableSentryReplay();
        }
    }, [organizationPlan, userCannotAccessCurrentOrg]);

    if (userCannotAccessCurrentOrg) {
        return <OrganizationAccessFallback />;
    }

    return (
        <CurrentOrganizationProvider organization={organization ?? null}>
            <SyncUserProfile authenticatedUser={authenticatedUser} />
            <EnforceSSOWrapper organization={organization}>{children}</EnforceSSOWrapper>
        </CurrentOrganizationProvider>
    );
}

/**
 * Sync the API user profile with the Firebase user.
 * When a user changes its email they are unsynced.
 * It should be handled server-side with webhooks but not sure it's possible.
 */
function SyncUserProfile(props: { authenticatedUser: User }) {
    const { authenticatedUser } = props;
    const user = useLoggedInUser();

    React.useEffect(() => {
        if (user.email && authenticatedUser.email && user.email !== authenticatedUser.email) {
            apiClient.users.syncProfileWithAuth().catch(() => {
                // It's not critical if it fails,
                // it will be retry when the user reloads.
            });
        }
    }, [user.email, authenticatedUser.email]);

    return null;
}

/**
 * Render any screen, overlay or main ones.
 */
function ScreenRouter(props: {
    screen: NavigationScreenState;
    isOverlay: boolean;
}): React.ReactElement {
    const { screen, isOverlay } = props;

    // Fallback to start hiding the splashscreen
    // Deeper components in the tree will hide without a delay when they are ready
    useHideSplashscreen();
    useUpdateOrgMemberLastSeenAt();

    // Warm the chunks of screens the user is likely to visit next, once this
    // screen has settled and the browser is idle.
    useNeighborPreload(getScreenNeighbors(screen.type));

    const organizationId = useCurrentOrganizationId();

    switch (screen.type) {
        case 'oauth_authorize':
            return <OAuthAuthorizeScreen screen={screen} />;
        case 'root':
            return <RootRedirect screen={screen} />;
        case 'org_settings':
            return <OrgSettingsScreenRouter screen={screen} isOverlay={isOverlay} />;
        case 'account':
            return (
                <AppLayout
                    sidebar={<SettingsSidebar screen={screen} />}
                    screen={screen}
                    isOverlay={isOverlay}
                >
                    <SettingsScreen screen={screen} />
                </AppLayout>
            );
        case 'org_join':
            return <JoinOrganizationScreen screen={screen} />;
        case 'org_invite':
            return <InvitesOrganizationScreen screen={screen} isOverlay={isOverlay} />;
        case 'site_invite':
            return <SiteInviteScreen screen={screen} isOverlay={isOverlay} />;
        case 'welcome':
            return <OnboardingFlow screen={screen} />;
        case 'org_with_basename':
            return <RedirectOrgWithBasename screen={screen} />;
        case 'integrations_legacy':
            return <IntegrationsLegacyRedirect screen={screen} />;
        case 'site_preview_auth':
            return <RedirectWithSitePreview screen={screen} />;
        case 'join':
            // If there is a user authenticated, but they authenticated using the join screen,
            // redirect to the welcome page so users can get started with setting up an org.
            return <NavigationRedirect to={{ screen: { type: 'root' } }} replace />;
        default:
            return (
                <UpgradeDialogProvider>
                    <AppLayout
                        sidebar={<ContentSidebar screen={screen} />}
                        screen={screen}
                        isOverlay={isOverlay}
                    >
                        {organizationId ? (
                            <OrganizationAccessPlaceholder organization={organizationId}>
                                <MainScreenSwitch screen={screen} />
                            </OrganizationAccessPlaceholder>
                        ) : (
                            <MainScreenSwitch screen={screen} />
                        )}
                    </AppLayout>
                </UpgradeDialogProvider>
            );
    }
}

/**
 * Render an organization settings screen.
 */
export function OrgSettingsScreenRouter(props: {
    screen: NavigationScreenOrgSettings;
    isOverlay?: boolean;
}) {
    const { screen, isOverlay } = props;

    return (
        <AppLayout
            sidebar={<SettingsSidebar screen={screen} />}
            screen={screen}
            isOverlay={isOverlay}
        >
            <SettingsScreen screen={screen} />
        </AppLayout>
    );
}

/**
 * Render a navigation screen inside the main UI with the sidebar.
 *
 * If you add or move a lazy screen in this switch, update
 * `Screens/routePreload.ts` so boot-time preloading stays in sync — drift
 * only costs the optimization, never correctness.
 */
function MainScreenSwitch(props: { screen: NavigationScreenState }): React.ReactElement | null {
    const { screen } = props;

    switch (screen.type) {
        case 'home':
            return <HomeScreen screen={screen} />;
        case 'site':
            return <SiteScreen screen={screen} />;
        case 'sites':
            return <SitesListScreen screen={screen} />;
        case 'space':
            return <SpaceScreen screen={screen} />;
        case 'collection':
            return <CollectionScreen screen={screen} />;
        case 'trash':
            return <TrashScreen screen={screen} />;
        case 'org_content':
            return <OrganizationContentScreen screen={screen} />;
        case 'styleguides':
            return <StyleguidesScreen screen={screen} />;
        case 'styleguide_new':
            return <StyleguideNewScreen screen={screen} />;
        case 'agents':
            return <AgentsScreen screen={screen} />;
        case 'change_requests':
            return <ChangeRequestsScreen screen={screen} />;
        case 'triage':
            return <TriageScreen screen={screen} />;
        case 'content_gaps':
            return <ContentGapsScreen screen={screen} />;
        case 'link_audit':
            return <LinkAuditScreen screen={screen} />;
        default:
            return <MainScreenFallback />;
    }
}

function MainScreenFallback() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Page />
            </EmptyStateIcon>
            <EmptyStateNotFoundHeading />
            <Description>The page you are looking for does not exist.</Description>
            <EmptyStateActions>
                <Button href={{ screen: { type: 'home' } }} label="Back to home" kind="secondary" />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Overlay that shows the full-screen loader when the app is about to reload.
 */

/**
 * A loading fallback that covers the full screen.
 */
function FullScreenLoadingFallback(props: { children?: React.ReactNode; className?: string }) {
    const { children, className } = props;
    return (
        <div
            className={clsx(
                'fixed inset-0 z-10 flex size-full flex-col items-center justify-center bg-base',
                className
            )}
            data-testid="app.fullScreenLoadingFallback"
        >
            <GitBookLoader />
            {children}
        </div>
    );
}

/**
 * Track feature flags changes in Amplitude.
 */
function TrackFeatureFlags() {
    const client = useClient();

    React.useEffect(() => {
        if (!client) {
            return;
        }

        // Identify the user when the feature flags are updated
        client.on('featuresUpdated', () => {
            const rawFlags = client.getFlags();
            const flags = Object.fromEntries(
                Object.entries(rawFlags).map(([key, value]) => [key, value?.isEnabled])
            ) as EnabledFeatureFlagsMap;
            analytics.identify({ flags });
        });
    }, [client]);

    React.useEffect(() => {
        if (!client) {
            return;
        }

        return withReflag(client);
    }, [client]);

    return null;
}
